import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useLang } from "../context/LanguageContext";

const LANG_LABEL = { it: "IT", en: "EN", de: "DE" };

const Navbar = () => {
  const { t, lang, setLang, supported } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/menu", label: t.nav.menu, testid: "nav-menu" },
    { to: "/gallery", label: t.nav.gallery, testid: "nav-gallery" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[color:var(--ivory)]/85 border-b border-[color:var(--line)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-32 lg:h-44">
        <Link
          to="/"
          data-testid="nav-brand"
          className="flex items-center"
        >
          <img
            src="/logo.jpg?v=2"
            alt="Caffè & Cucina Sorriso"
            className="h-28 lg:h-40 w-auto object-contain ring-1 ring-[color:var(--wine)]/15 shadow-lg shadow-[color:var(--wine)]/10"
            data-testid="brand-logo"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              end={l.to === "/"}
              className={({ isActive }) =>
                `overline link-underline ${
                  isActive ? "text-[color:var(--wine)]" : "text-[color:var(--ink-2)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="language-switcher"
                className="overline flex items-center gap-1 px-3 py-2 hover:text-[color:var(--wine)] transition-colors"
              >
                {LANG_LABEL[lang]}
                <ChevronDown size={12} strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[color:var(--ivory)] border border-[color:var(--line)] rounded-none min-w-[100px]"
            >
              {supported.map((code) => (
                <DropdownMenuItem
                  key={code}
                  data-testid={`lang-option-${code}`}
                  onClick={() => setLang(code)}
                  className="overline cursor-pointer focus:bg-[color:var(--ivory-2)] rounded-none"
                >
                  {LANG_LABEL[code]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/contact"
            data-testid="nav-book-button"
            className="hidden md:inline-flex btn-primary"
            style={{ padding: "0.85rem 1.4rem" }}
          >
            {t.nav.contact}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[color:var(--ivory)] border-t border-[color:var(--line)]"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className="font-serif-display text-3xl"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-book-button-mobile"
              className="btn-primary mt-4 self-start"
            >
              {t.nav.contact}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
